import { HpClientDebugFlags, hasFlag, log } from 'clientLogging';
import type {
  HpClientHeightChangeMessageData,
  HpMessage,
  HpMessageCommon,
  HpMessageContent,
  HpMessageType,
  HpPaymentOutcomeMessageData
} from 'interfaces/messageTypes';
import { HP_MESSAGE_TYPE } from 'interfaces/messageTypes';
import type { HpDispatchFunction } from './actionTypes';
import { RootState } from 'store/configureStore';
import { AUDIT_TYPE } from 'interfaces/auditTypes';
import { sendAuditEvent } from 'utils';

type HpDispatch = HpDispatchFunction<any>;
type HandlerCtx = {
  dispatch: HpDispatch;
  getState: () => RootState;
  raw: MessageEvent<any>;
  data: HpMessageCommon;
};

export type HpMessageHandler = (ctx: HandlerCtx) => void;
export type HpHandlers = Partial<Record<HpMessageType, HpMessageHandler>>;

export const defaultPaymentHandlers: Record<HpMessageType, HpMessageHandler> = {
  [HP_MESSAGE_TYPE.CLIENT_HEIGHT_CHANGE]: ({ dispatch, data }) => {
    const { height } = data as HpClientHeightChangeMessageData;
    dispatch({ type: 'CLIENT_HEIGHT_CHANGE', payload: height });
  },
  [HP_MESSAGE_TYPE.PAYMENT_ATTEMPT]: ({ dispatch }) => {
    dispatch({ type: 'PAYMENT_LOADING' });
  },
  [HP_MESSAGE_TYPE.ADDRESS_FORM_DISPLAYED]: ({ dispatch }) => {
    dispatch({ type: 'ADDRESS_FORM_DISPLAYED' });
  },
  [HP_MESSAGE_TYPE.BRAINTREE_LOADING]: ({ dispatch }) => {
    dispatch({ type: 'BRAINTREE_LOADING' });
  },
  [HP_MESSAGE_TYPE.ERROR_PAGE_LOADED]: ({ dispatch }) => {
    dispatch({ type: 'PORTAL_LOADED' });
  },
  [HP_MESSAGE_TYPE.BRAINTREE_LOADED]: ({ dispatch }) => {
    dispatch({ type: 'PORTAL_LOADED' });
  },
  [HP_MESSAGE_TYPE.PAYMENT_OUTCOME]: ({ dispatch, getState, data }) => {
    const { paymentReference, paymentStatus, success, cardLastFour, cardType, cardImageUrl, expiryDate } =
      data as HpPaymentOutcomeMessageData;

    const state = getState();
    const { policyNumber, historyId, policyDetailsId } = state.customerData.selectedPolicy || {};

    if (success) {
      dispatch({
        type: 'PAYMENT_SUCCESS',
        payload: { paymentReference, cardLastFour, cardType, cardImageUrl, expiryDate }
      });
      if (policyDetailsId && historyId && policyNumber) {
        sendAuditEvent({
          activityId: AUDIT_TYPE.PAYMENT_SUCCESS_MESSAGE_RECEIVED,
          policyDetailsId,
          historyId,
          policyNumber
        });
      }
    } else {
      dispatch({ type: 'PAYMENT_ERROR', payload: paymentStatus });
      if (policyDetailsId && historyId && policyNumber) {
        sendAuditEvent({
          activityId: AUDIT_TYPE.PAYMENT_FAILURE_MESSAGE_RECEIVED,
          policyDetailsId,
          historyId,
          policyNumber
        });
      }
    }
  }
};

export function getPaymentStatusAction(
  message: MessageEvent<any>,
  overrides?: HpHandlers,
  onUnknown?: HpMessageHandler
) {
  return (dispatch: HpDispatch, getState: () => RootState): void => {
    log.info().enabledIf(hasFlag(HpClientDebugFlags.ReducerProcessor)).any('data', message.data).send();

    const { data: messageData = {} as HpMessageCommon } = message as HpMessage<HpMessageContent>;
    const { type } = messageData;

    const handlers = { ...defaultPaymentHandlers, ...overrides } as Record<string, HpMessageHandler>;
    const handler = type ? handlers[type] : undefined;

    if (handler) {
      handler({ dispatch, getState, raw: message, data: messageData });
    } else {
      if (onUnknown) {
        onUnknown({ dispatch, getState, raw: message, data: messageData });
      } else {
        dispatch({ type: 'UNKNOWN_PAYMENT_MESSAGE' });
      }
    }
  };
}
